<template>
    <div>
        <kgr-access-medium-scanner
            :club-id="clubId"
            access-medium-scanned-as-event
            @accessMediumScanned="onAccessMediumScanned($event)"
        />

        <kgr-form-scaffold
            :with-primary-buttons="false"
            @backBtnClicked="redirectToOverview"
        >
            <div class="kgr-row">
                <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                    <div class="kgr-grid__cell">
                        <div class="kgr-inputGroup">
                            <label class="kgr-inputGroup__label">{{ $t('Stichtag') }}</label>
                            <kgr-date-picker
                                :value="selectedDate.format('DD.MM.YYYY')"
                                @change="onDateSelected($event)"
                            />
                        </div>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-text-input
                            v-model="search"
                            :label="$t('Mitgliedersuche')"
                            @post-input="inputSearch"
                        />
                    </div>
                </div>

                <div class="kgr-grid__cell top-space">
                    <checkbox
                        :checked="memberWithAttendancesOnly"
                        :label="$t('Nur Mitglieder mit Anwesenheiten anzeigen')"
                        @change="onMemberWithAttendancesOnly()"
                    />
                </div>

                <div class="kgr-grid__cell top-space">
                    <checkbox
                        :checked="memberWithOpenAttendancesOnly"
                        :label="$t('Nur aktuell anwesende Mitglieder anzeigen')"
                        @change="onMemberWithOpenAttendancesOnly()"
                    />
                </div>
            </div>

            <div class="kgr-row">
                <loading
                    v-if="pendingLoadMemberAttendances"
                />

                <kgr-member-attendances
                    v-else-if="showAttendances"
                    :attendances-by-members="memberAttendances"
                    :is-current-day="isCurrentDay"
                    :pending-actions="pendingActions"
                    :attendance-in-edit-mode="attendanceInEditMode"
                    :pending-attendances="pendingAttendances"
                    class="animated fadeIn"
                    @cancelEdit="attendanceInEditMode = null"
                    @actionTriggered="triggerAttendanceAction($event)"
                    @editAttendance="attendanceInEditMode = $event"
                    @attendanceUpdated="onAttendanceUpdated($event)"
                    @attendanceRemoved="onAttendanceRemoved($event)"
                />
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import AccessMediumScanner from '../../components/club/access-medium/AccessMediumScanner';
import Checkbox from '../../components/common/form/checkbox/Checkbox';
import DatePicker from '../../components/common/form/picker/DatePicker';
import KgrFormScaffold from '../../components/common/form/FormScaffold';
import KgrMemberAttendances from '../../components/members/member-attendances/MemberAttendances';
import KgrTextInput from '../../components/common/form/input/TextInput.vue';
import Loading from '../../components/common/Loading';
import MemberRepository from '../../js/repository/MemberRepository';
import TeamRepository from '../../js/repository/TeamRepository';
import moment from 'moment';

export default {
    components: {
        KgrTextInput,
        Checkbox,
        Loading,
        'kgr-access-medium-scanner': AccessMediumScanner,
        KgrMemberAttendances,
        'kgr-date-picker': DatePicker,
        'kgr-form-scaffold': KgrFormScaffold,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        teamId: {
            type: Number,
            required: true,
        },
        teamName: {
            type: String,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            selectedDate: moment(),
            pendingActions: [],
            pendingAttendances: [],
            pendingLoadMemberAttendances: true,
            memberAttendances: [],
            memberWithAttendancesOnly: false,
            memberWithOpenAttendancesOnly: false,
            attendanceInEditMode: null,
            showAttendances: true,
            search: '',
            searchTimer: null,
        }
    },
    computed: {
        isCurrentDay() {
            return moment().format('DD.MM.YYYY') === this.selectedDate.format('DD.MM.YYYY');
        },
    },
    mounted() {
        this.loadData();
    },
    methods: {
        async loadData() {
            this.memberAttendances = [];
            TeamRepository.findMemberAttendancesForDay(
                this.teamId,
                this.selectedDate.format('YYYY-MM-DD'),
                this.search,
                this.memberWithAttendancesOnly,
                this.memberWithOpenAttendancesOnly
            ).then(memberAttendances => {
                this.memberAttendances = memberAttendances;
                this.pendingLoadMemberAttendances = false;
            }).catch(() => {
                this.pendingLoadMemberAttendances = false;
                this.$notify.error('error.loadingData');
            });
        },
        onAttendanceUpdated(event) {
            this.pendingAttendances.push(event.attendanceId);
            TeamRepository.updateMemberAttendance(event.attendanceId, event.start, event.end)
                .then(() => {
                    if (this.attendanceInEditMode === event.attendanceId) {
                        this.attendanceInEditMode = null;
                    }
                    this.removePendingAttendance(event.attendanceId);
                    this.$notify.success('Anwesenheitszeit erfolgreich gespeichert');
                    this.loadData();
                })
                .catch(() => this.$notify.error('saveChanges.error'));

        },
        onAttendanceRemoved(event) {
            this.pendingAttendances.push(event.attendanceId);
            TeamRepository.removeMemberAttendance(event.attendanceId)
                .then(() => {
                    this.$notify.success('Anwesenheitszeit erfolgreich entfernt');
                    this.loadData()
                })
                .catch(() => this.$notify.error('saveChanges.error'));

        },
        removePendingAttendance(attendanceId) {
            const index = this.pendingAttendances.indexOf(attendanceId);
            if (index !== -1) {
                this.pendingAttendances.splice(index, 1);
            }
        },
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        onDateSelected(selection) {
            this.selectedDate = moment(selection, 'DD-MM-YYYY');
            this.pendingLoadMemberAttendances = true;
            this.loadData();
        },
        onMemberWithAttendancesOnly() {
            this.memberWithAttendancesOnly = !this.memberWithAttendancesOnly
            if (!this.memberWithAttendancesOnly) {
                this.memberWithOpenAttendancesOnly = false;
            }
            this.loadData();
        },
        onMemberWithOpenAttendancesOnly() {
            this.memberWithOpenAttendancesOnly = !this.memberWithOpenAttendancesOnly
            if (this.memberWithOpenAttendancesOnly) {
                this.memberWithAttendancesOnly = true;
            }
            this.loadData();
        },
        inputSearch() {
            if ((this.search.trim().length) < 3) {
                return;
            }
            clearTimeout(this.searchTimer);

            this.searchTimer = setTimeout(function () {
                this.loadData();
            }.bind(this), 600);
        },
        onAccessMediumScanned(memberId) {
            this.triggerAttendanceAction(memberId, (resolve) => resolve());
        },
        triggerAttendanceAction(memberId) {
            if (!this.isCurrentDay) {
                return;
            }

            if (this.pendingActions.includes(memberId)) {
                return;
            }

            this.pendingActions.push(memberId);

            MemberRepository
                .triggerTeamAttendanceAction(memberId, this.teamId)
                .then(() => {
                    this.removeMemberIdFromPendingActions(memberId);
                    this.$notify.success('Anwesenheitszeit erfolgreich gespeichert');
                    this.loadData();
                })
                .catch(error => {
                    this.removeMemberIdFromPendingActions(memberId);
                    if (error.status === 400) {
                        this.$notify.error('memberAttendances.memberNotActive', {name: this.teamName})
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
        },
        removeMemberIdFromPendingActions(memberId) {
            this.pendingActions = this.pendingActions.filter(pendingId => pendingId !== memberId);
        },
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
    .date-center > * {
        text-align: center;
    }

    .top-space {
        padding-top: 27px;
    }
</style>
